import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Upload, ExternalLink, RotateCcw, SkipForward, UserCog } from 'lucide-react';
import api from '../../services/api';
import { field, ghost, primary, muted, rowBorder, errorText } from '../time/styles';
import { viewUrl, ACCEPT } from './docUtils';

// One checklist task, opened: what to do, and the way to do it (form, upload,
// tick, clearance, or a link to the letter to sign). HR also reviews here.

const Question = ({ q, value, error, onChange }) => {
    const id = `q-${q.key}`;
    const common = { id, 'aria-required': !!q.required, 'aria-invalid': !!error, 'aria-describedby': error ? `${id}-err` : undefined };
    let input;
    if (q.type === 'longtext' || q.type === 'LONGTEXT') input = <textarea {...common} style={{ ...field, minHeight: 70 }} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
    else if (q.type === 'CHOICE') input = <select {...common} style={field} value={value ?? ''} onChange={e => onChange(e.target.value)}><option value="">Choose…</option>{q.options.map(o => <option key={o} value={o}>{o}</option>)}</select>;
    else if (q.type === 'YESNO') input = <select {...common} style={field} value={value === true ? 'yes' : value === false ? 'no' : ''} onChange={e => onChange(e.target.value === '' ? null : e.target.value === 'yes')}><option value="">Choose…</option><option value="yes">Yes</option><option value="no">No</option></select>;
    else if (q.type === 'RATING') input = <select {...common} style={field} value={value ?? ''} onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}><option value="">Choose…</option>{[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}</select>;
    else if (q.type === 'NUMBER') input = <input {...common} type="number" style={field} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
    else input = <input {...common} type={q.type === 'date' || q.type === 'DATE' ? 'date' : q.type === 'email' ? 'email' : q.type === 'tel' ? 'tel' : 'text'} style={field} value={value ?? ''} onChange={e => onChange(e.target.value)} autoComplete="off" />;
    return (
        <div style={{ display: 'grid', gap: 4 }}>
            <label htmlFor={id} style={{ color: 'var(--text-main)', fontSize: 14 }}>{q.label}{q.required && <span style={{ color: '#ef4444' }}> *</span>}</label>
            {input}
            {q.hint && <span style={muted}>{q.hint}</span>}
            {error && <span id={`${id}-err`} style={{ color: '#ef4444', fontSize: 13 }}>{error}</span>}
        </div>
    );
};

const FormTask = ({ task, editable, onDone, say }) => {
    const [answers, setAnswers] = useState(task.answers || {});
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    if (!task.form) return <p style={muted}>The form for this task no longer exists. Ask HR.</p>;
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post(`/lifecycle/tasks/${task.id}/form`, { answers });
            setErrors({});
            say(true, r.data.status === 'SUBMITTED' ? 'Sent to HR to check' : 'Saved');
            onDone();
        } catch (err) {
            setErrors(err.response?.data?.fields || {});
            say(false, errorText(err, 'Could not save the form'));
        } finally {
            setBusy(false);
        }
    };
    const groups = [];
    for (const q of task.form.questions) {
        const g = groups.find(x => x.section === q.section) || (groups.push({ section: q.section, qs: [] }), groups[groups.length - 1]);
        g.qs.push(q);
    }
    return (
        <form onSubmit={submit} style={{ display: 'grid', gap: 16 }}>
            {groups.map(g => (
                <fieldset key={g.section} disabled={!editable} style={{ border: rowBorder, borderRadius: 6, padding: 12, display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                    <legend style={{ ...muted, fontWeight: 600 }}>{g.section}</legend>
                    {g.qs.map(q => <Question key={q.key} q={q} value={answers[q.key]} error={errors[q.key]} onChange={v => setAnswers(p => ({ ...p, [q.key]: v }))} />)}
                </fieldset>
            ))}
            {editable && <div><button className="btn btn-primary" style={primary} disabled={busy}><Check size={14} /> {busy ? 'Saving…' : 'Submit'}</button></div>}
        </form>
    );
};

const UploadTask = ({ task, onDone, say }) => {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (file && file.size > 10 * 1024 * 1024) { say(false, 'The file must be under 10 MB'); return; }
        const data = new FormData();
        data.append('file', file);
        setBusy(true);
        try {
            const r = await api.post(`/lifecycle/tasks/${task.id}/upload`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
            say(true, r.data.status === 'SUBMITTED' ? 'Uploaded and sent to HR to check' : 'Uploaded');
            onDone();
        } catch (err) {
            say(false, errorText(err, 'Could not upload the file'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={submit} style={{ display: 'grid', gap: 8 }}>
            <input type="file" aria-label="File to upload" accept={ACCEPT} required onChange={e => setFile(e.target.files[0] || null)} />
            <span style={muted}>PDF, PNG, JPEG or WebP, up to 10 MB. It is saved in the employee's documents.</span>
            <div><button className="btn btn-primary" style={primary} disabled={busy || !file}><Upload size={14} /> {busy ? 'Uploading…' : 'Upload'}</button></div>
        </form>
    );
};

const CompleteTask = ({ task, onDone, say, label = 'Mark as done' }) => {
    const [comment, setComment] = useState('');
    const [amount, setAmount] = useState('');
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post(`/lifecycle/tasks/${task.id}/complete`, { comment, recoveryAmount: task.type === 'CLEARANCE' && amount !== '' ? Number(amount) : undefined });
            say(true, `Done: ${task.title}`);
            onDone();
        } catch (err) {
            say(false, errorText(err, 'Could not complete the task'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={submit} style={{ display: 'grid', gap: 8, maxWidth: 520 }}>
            {task.type === 'CLEARANCE' && (
                <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Amount to recover in the final settlement (leave empty if none)</span>
                    <input type="number" min="0" step="0.01" aria-label="Amount to recover" style={{ ...field, width: 180 }} value={amount} onChange={e => setAmount(e.target.value)} />
                </label>
            )}
            <label style={{ display: 'grid', gap: 4 }}><span style={muted}>Note (optional)</span>
                <input aria-label="Note" maxLength={1000} style={field} value={comment} onChange={e => setComment(e.target.value)} />
            </label>
            <div><button className="btn btn-primary" style={primary} disabled={busy}><Check size={14} /> {task.type === 'CLEARANCE' ? 'Sign off' : label}</button></div>
        </form>
    );
};

/** HR: accept or send back a submission; skip; close on someone's behalf. */
const HrActions = ({ task, onDone, say }) => {
    const [comment, setComment] = useState('');
    const [people, setPeople] = useState(null); // loaded when HR chooses to reassign
    const [target, setTarget] = useState('');
    const openReassign = () => api.get('/admin/users').then(r => setPeople(r.data.filter(u => u.isActive !== false && u.id !== task.case?.user?.id))).catch(e => say(false, errorText(e, 'Could not load people')));
    const act = async (fn, okText) => {
        try { await fn(); say(true, okText); } catch (err) { say(false, errorText(err, 'That did not work')); }
        onDone(); // reload the task either way, so the screen shows what is true now
    };
    const open = ['PENDING', 'SENT_BACK', 'SUBMITTED'].includes(task.status);
    if (!open) return null;
    return (
        <div style={{ display: 'grid', gap: 8, borderTop: rowBorder, paddingTop: 12, marginTop: 4 }}>
            <strong style={{ fontSize: 14, color: 'var(--text-main)' }}>HR</strong>
            <input aria-label="Comment for the employee" placeholder={task.status === 'SUBMITTED' ? 'Comment (needed to send it back)' : 'Reason (needed to skip)'} maxLength={1000} style={field} value={comment} onChange={e => setComment(e.target.value)} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {task.status === 'SUBMITTED' && <>
                    <button className="btn btn-primary" style={primary} onClick={() => act(() => api.post(`/lifecycle/tasks/${task.id}/review`, { decision: 'ACCEPT', comment }), `Accepted: ${task.title}`)}><Check size={14} /> Accept</button>
                    <button style={ghost} onClick={() => act(() => api.post(`/lifecycle/tasks/${task.id}/review`, { decision: 'SEND_BACK', comment }), 'Sent back')}><RotateCcw size={14} /> Send back</button>
                </>}
                <button style={ghost} onClick={() => act(() => api.post(`/lifecycle/tasks/${task.id}/skip`, { reason: comment }), `Skipped: ${task.title}`)}><SkipForward size={14} /> Not needed</button>
                {task.ownerType !== 'EMPLOYEE' && !people && <button style={ghost} onClick={openReassign}><UserCog size={14} /> Reassign</button>}
            </div>
            {people && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select aria-label="Reassign to" autoFocus style={field} value={target} onChange={e => setTarget(e.target.value)}>
                        <option value="">Choose who does it…</option>
                        <optgroup label="A team">{['HR', 'ADMIN', 'FINANCE'].map(r => <option key={r} value={`role:${r}`}>{{ HR: 'HR team', ADMIN: 'Admins', FINANCE: 'Finance team' }[r]}</option>)}</optgroup>
                        <optgroup label="A person">{people.map(u => <option key={u.id} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>
                    </select>
                    <button className="btn btn-primary" style={primary} disabled={!target} onClick={async () => { const [k, v] = target.split(':'); await act(() => api.post(`/lifecycle/tasks/${task.id}/reassign`, k === 'role' ? { role: v } : { userId: v }), `Reassigned: ${task.title}`); setPeople(null); setTarget(''); }}>Reassign</button>
                    <button style={ghost} onClick={() => { setPeople(null); setTarget(''); }}>Cancel</button>
                </div>
            )}
        </div>
    );
};

const TaskPanel = ({ taskId, hr, onChanged, say }) => {
    const [task, setTask] = useState(null);
    const [version, setVersion] = useState(0);
    useEffect(() => {
        let live = true;
        api.get(`/lifecycle/tasks/${taskId}`).then(r => { if (live) setTask(r.data); }).catch(e => { if (live) say(false, errorText(e, 'Could not open the task')); });
        return () => { live = false; };
    }, [taskId, version, say]);
    const done = () => { setVersion(v => v + 1); onChanged(); };
    if (!task) return <p style={muted}>Loading…</p>;
    const open = task.case.status === 'OPEN' && ['PENDING', 'SENT_BACK'].includes(task.status);
    // Nobody acts as HR on their own checklist.
    const me = JSON.parse(localStorage.getItem('user') || '{}').id;
    const ownCase = task.case.user.id === me;
    const actAsHr = hr && !ownCase;
    return (
        <div style={{ display: 'grid', gap: 12, padding: '12px 0' }}>
            {task.instructions && <p style={{ margin: 0, whiteSpace: 'pre-line', color: 'var(--text-main)' }}>{task.instructions}</p>}
            {task.note && <p style={{ ...muted, margin: 0 }}>{task.note}</p>}
            {task.status === 'SENT_BACK' && task.reviewComment && <p role="alert" style={{ margin: 0, color: '#d97706' }}>HR asked for changes: {task.reviewComment}</p>}
            {task.status === 'SUBMITTED' && <p style={{ ...muted, margin: 0 }}>Waiting for HR to check.</p>}
            {task.comment && task.status === 'DONE' && <p style={{ ...muted, margin: 0 }}>Note: {task.comment}</p>}
            {task.recoveryAmount !== null && task.recoveryAmount !== undefined && <p style={{ ...muted, margin: 0 }}>To recover: {task.recoveryAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>}
            {task.document && <a style={{ ...ghost, width: 'fit-content' }} href={viewUrl(task.document.id)} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} /> Open {task.document.originalName || task.document.title}</a>}

            {task.type === 'FORM' && (open || task.answers) && <FormTask task={task} editable={open} onDone={done} say={say} />}
            {task.type === 'UPLOAD' && open && <UploadTask task={task} onDone={done} say={say} />}
            {(task.type === 'TICK' || task.type === 'CLEARANCE') && open && <CompleteTask task={task} onDone={done} say={say} />}
            {task.type === 'SIGN' && (ownCase ? (
                task.letter
                    ? <p style={{ margin: 0 }}>{task.letter.status === 'SIGNED' ? 'Signed.' : <>The letter is ready: <Link to="/documents">sign it in My documents</Link>.</>}</p>
                    : <p style={{ ...muted, margin: 0 }}>HR has not issued this letter yet. It will appear in My documents.</p>
            ) : (
                <p style={{ margin: 0 }}>
                    {!task.letter && <>Not issued yet. <Link to={`/admin/documents?userId=${task.case.user.id}`}>Issue it from Documents</Link>; this task closes when {task.case.user.name} signs it.</>}
                    {task.letter?.status === 'AWAITING_SIGNATURE' && <>Issued, waiting for {task.case.user.name} to sign.</>}
                    {task.letter?.status === 'SIGNED' && 'Signed.'}
                </p>
            ))}
            {actAsHr && open && task.type === 'SIGN' && <CompleteTask task={task} onDone={done} say={say} label="Mark as signed (on paper)" />}
            {actAsHr && task.case.status === 'OPEN' && <HrActions task={task} onDone={done} say={say} />}
        </div>
    );
};

export default TaskPanel;
